import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import Plot from "react-plotly.js";
import { Box, Button, Stack, RadioGroup, FormControlLabel, Radio, FormLabel, Select, MenuItem, InputLabel, FormControl, Typography } from "@mui/material";
import TrackRangeSlider from "./TrackRangeSlider";
import RaceLineHighlight from "./RaceLineHighlight";
import { detectTrackForLap } from '../utils/trackDetection';

// Helper to interpolate between two points
function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Helper to interpolate between two telemetry points
function interpolatePoint(p1, p2, t) {
  return {
    position_x: lerp(p1.position_x, p2.position_x, t),
    position_z: lerp(p1.position_z, p2.position_z, t),
  };
}

// Helper to get the time (in seconds) for each telemetry point
function getTimeArray(lapArr, freq) {
  if (!lapArr || lapArr.length === 0) return [];
  // If no timestamp field, generate synthetic time using estimated frequency
  if (!('timestamp' in lapArr[0])) {
    const dt = 1 / (freq || 60); // fallback to 60Hz if freq not provided
    return Array.from({length: lapArr.length}, (_, i) => i * dt);
  }
  let times = [0];
  for (let i = 1; i < lapArr.length; i++) {
    const dt = (lapArr[i].timestamp - lapArr[i - 1].timestamp) || 0.01;
    times.push(times[times.length - 1] + dt);
  }
  return times;
}

export default function RaceLineReplay({
  timeRange,
  setTimeRange,
  handleResetTimeRange,
  selectedLaps,
  lapData,
  trackLayout,
  normalizedTracks, // NEW PROP
  onAutoDetectTrack, // NEW PROP
  onTrackDetectStatus, // NEW PROP
  selectedTrack, // keep prop for future, but do not use for suppression
  corners = [], // NEW PROP
}) {
  // --- State ---
  const [trackBorders, setTrackBorders] = useState(null);
  const [fsAxisRanges, setFsAxisRanges] = useState(null);
  const [fsUserZoomed, setFsUserZoomed] = useState(false);
  const [trackBBox, setTrackBBox] = useState({ xMin: 0, xMax: 1, yMin: 0, yMax: 1 });
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [replayTime, setReplayTime] = useState(0); // seconds
  const [distanceTraveled, setDistanceTraveled] = useState(0); // meters
  const [axisRanges, setAxisRanges] = useState({ x: null, y: null });
  const [currentIndices, setCurrentIndices] = useState([]); // One per lap
  const [replayElapsedArr, setReplayElapsedArr] = useState([]); // One per lap
  const [replaySpeed, setReplaySpeed] = useState(1); // 0.5, 1, 2, 4
  const [highlightLapId, setHighlightLapId] = useState("");
  const [autoTrackName, setAutoTrackName] = useState(null);
  const [autoTrackBorder, setAutoTrackBorder] = useState(null);
  const [trackDetectDebug, setTrackDetectDebug] = useState(null);
  const [selectedCornerIdx, setSelectedCornerIdx] = useState(null); // NEW STATE
  // Set default highlight lap to 'None' on mount and when laps change
  useEffect(() => {
    setHighlightLapId("");
  }, [selectedLaps]);
  const animationRef = useRef();
  const animationStartRef = useRef(null); // <-- Persist animation start time

  // --- Colors ---
  const colors = {
    background: '#1E1E1E',
    grid: '#333333',
    text: '#FFFFFF',
    lines: [
      '#00AEEF', '#FFB800', '#9000FF', '#FF6B6B', '#4ECDC4', '#FFD93D', '#FF7F50', '#7B68EE',
    ],
    border: 'rgba(200,200,200,0.5)'
  };

  // --- Track border loading ---
  useEffect(() => {
    if (!trackLayout) {
      setTrackBorders(null);
      return;
    }
    // Always set track borders if trackLayout is available
    const convert = arr => Array.isArray(arr) && arr.length && arr[0].position_x !== undefined
      ? arr.map(p => [p.position_x, p.position_z])
      : arr;
    setTrackBorders({
      inside: convert(trackLayout.inside),
      outside: convert(trackLayout.outside)
    });
    setError(null);
    // Optionally, show error if laps are needed for lap lines
    if (!selectedLaps || !Array.isArray(selectedLaps) || selectedLaps.length === 0) {
      setError("No laps selected for visualization.");
    }
    if (!lapData || typeof lapData !== 'object') {
      setError("Lap data is missing or invalid.");
    }
  }, [trackLayout, selectedLaps, lapData]);

  // --- Auto-detect track when a lap is selected ---
  useEffect(() => {
    // Only run detection if all required data is loaded
    if (!selectedLaps.length || !lapData || !normalizedTracks) {
      setAutoTrackName(null);
      setAutoTrackBorder(null);
      setTrackDetectDebug(null);
      if (onAutoDetectTrack) onAutoDetectTrack(null); // Notify parent
      if (onTrackDetectStatus) onTrackDetectStatus(null); // Suppress message during loading/reset
      return;
    }
    // Ensure lap data is loaded for the selected lap
    const lapArr = lapData[selectedLaps[0]];
    if (!lapArr || !Array.isArray(lapArr) || lapArr.length === 0) {
      if (onTrackDetectStatus) onTrackDetectStatus(null); // Suppress message while lap is loading
      return;
    }
    let best = null, bestMSE = Infinity;
    const lapNorm = require('../utils/trackDetection').normalizeLapPath(lapArr);
    for (const [trackName, borders] of Object.entries(normalizedTracks)) {
      if (!borders || !borders.inside) continue;
      const mse = require('../utils/trackDetection').pathMSE(lapNorm, borders.inside);
      if (mse < bestMSE) {
        bestMSE = mse;
        best = trackName;
      }
    }
    setTrackDetectDebug({ best, bestMSE, lapNorm });
    const match = (bestMSE < 0.01) ? best : null;
    setAutoTrackName(match);
    if (onAutoDetectTrack) onAutoDetectTrack(match); // Notify parent
    if (onTrackDetectStatus) {
      if (!match) {
        onTrackDetectStatus('No matching track found for selected lap.');
      } else {
        onTrackDetectStatus(null);
      }
    }
    if (match && normalizedTracks[match]) {
      const borders = normalizedTracks[match];
      setAutoTrackBorder({
        inside: borders.inside ? {
          x: borders.inside.map(p => p[0]),
          y: borders.inside.map(p => p[1]),
        } : null,
        outside: borders.outside ? {
          x: borders.outside.map(p => p[0]),
          y: borders.outside.map(p => p[1]),
        } : null,
      });
    } else {
      setAutoTrackBorder(null);
    }
  }, [selectedLaps, lapData, normalizedTracks]);

  // Handler: when a corner is selected, set the slider range
  const handleCornerSelect = (idx) => {
    setSelectedCornerIdx(idx);
    setFsUserZoomed(false); // Reset manual zoom on new corner selection
    if (corners && corners[idx]) {
      setTimeRange({ start: corners[idx].start_pct, end: corners[idx].end_pct });
    }
  };

  useEffect(() => {
    setFsUserZoomed(false); // Reset manual zoom on time range change
  }, [timeRange]);

  // --- Defensive track range ---
  let safeTrackRange = timeRange && typeof timeRange === 'object' && typeof timeRange.start === 'number' && typeof timeRange.end === 'number'
    ? { start: Math.max(0, Math.min(timeRange.start, 100)), end: Math.max(0, Math.min(timeRange.end, 100)) }
    : { start: 0, end: 100 };
  if (safeTrackRange.start >= safeTrackRange.end) {
    safeTrackRange = { start: 0, end: 100 };
  }

  // --- Helpers for lap slicing ---
  function getCumulativeDistances(lapArr) {
    if (!Array.isArray(lapArr) || lapArr.length === 0) return [];
    let dists = [0];
    for (let i = 1; i < lapArr.length; i++) {
      const p1 = lapArr[i - 1];
      const p2 = lapArr[i];
      const dx = (p2.position_x - p1.position_x) || 0;
      const dz = (p2.position_z - p1.position_z) || 0;
      dists.push(dists[dists.length - 1] + Math.sqrt(dx * dx + dz * dz));
    }
    return dists;
  }
  function findClosestIdx(dists, target) {
    let idx = 0;
    let minDiff = Math.abs(dists[0] - target);
    for (let i = 1; i < dists.length; i++) {
      const diff = Math.abs(dists[i] - target);
      if (diff < minDiff) {
        minDiff = diff;
        idx = i;
      }
    }
    return idx;
  }
  function getDistanceSliceIndices(lapData, selectedLaps, safeTrackRange) {
    return selectedLaps.map(lapId => {
      const arr = lapData[lapId];
      const dists = getCumulativeDistances(arr);
      const totalDist = dists[dists.length - 1];
      const startDist = (safeTrackRange.start / 100) * totalDist;
      const endDist = (safeTrackRange.end / 100) * totalDist;
      const startIdx = findClosestIdx(dists, startDist);
      const endIdx = Math.max(startIdx + 1, findClosestIdx(dists, endDist));
      return { startIdx, endIdx, startDist, endDist };
    });
  }

  // --- Memoized slice indices ---
  const distanceSliceIndices = React.useMemo(() => getDistanceSliceIndices(lapData, selectedLaps, safeTrackRange), [lapData, selectedLaps, safeTrackRange]);

  // Zoom to filtered race line when selectedCornerIdx or distanceSliceIndices change
  useEffect(() => {
    if (
      selectedCornerIdx !== null &&
      selectedLaps.length > 0 &&
      lapData[selectedLaps[0]] &&
      distanceSliceIndices[0] &&
      !fsUserZoomed // Only auto-zoom if user hasn't zoomed manually
    ) {
      const lapArr = lapData[selectedLaps[0]];
      const { startIdx, endIdx } = distanceSliceIndices[0];
      const filtered = lapArr.slice(startIdx, endIdx + 1);
      if (filtered.length > 0) {
        const xVals = filtered.map(p => p.position_x);
        const yVals = filtered.map(p => -p.position_z);
        let xMin = Math.min(...xVals);
        let xMax = Math.max(...xVals);
        let yMin = Math.min(...yVals);
        let yMax = Math.max(...yVals);
        // Add 10% padding to each axis
        const xPad = (xMax - xMin) * 0.1;
        const yPad = (yMax - yMin) * 0.1;
        xMin -= xPad;
        xMax += xPad;
        yMin -= yPad;
        yMax += yPad;
        setFsAxisRanges({ x: [xMin, xMax], y: [yMin, yMax] });
      }
    }
  }, [selectedCornerIdx, distanceSliceIndices, selectedLaps, lapData, fsUserZoomed]);

  // Reset selected corner if corners change
  useEffect(() => { setSelectedCornerIdx(null); }, [corners]);

  // Helper to check if a track is selected and a lap is loaded
  function selectedTrackIsSet(selectedLaps, lapData) {
    return selectedLaps.length && selectedLaps[0] && lapData[selectedLaps[0]];
  }

  // --- Range start/end for current lap (first lap as reference) ---
  const rangeStart = React.useMemo(() => {
    if (!selectedLaps.length || !lapData[selectedLaps[0]]) return 0;
    const arr = lapData[selectedLaps[0]];
    const dists = getCumulativeDistances(arr);
    const { startIdx } = distanceSliceIndices[0] || {};
    return dists[startIdx] || 0;
  }, [selectedLaps, lapData, distanceSliceIndices]);
  const rangeEnd = React.useMemo(() => {
    if (!selectedLaps.length || !lapData[selectedLaps[0]]) return 0;
    const arr = lapData[selectedLaps[0]];
    const dists = getCumulativeDistances(arr);
    const { endIdx } = distanceSliceIndices[0] || {};
    return dists[endIdx] || 0;
  }, [selectedLaps, lapData, distanceSliceIndices]);

  // --- Reset distanceTraveled to rangeStart on range/lap change ---
  useEffect(() => {
    setDistanceTraveled(rangeStart);
    setIsPlaying(false);
  }, [rangeStart, rangeEnd, selectedLaps]);

  // --- Reset currentIndices and replayElapsedArr to range start on range/lap change ---
  useEffect(() => {
    if (!selectedLaps.length || !lapData[selectedLaps[0]]) return;
    const indices = selectedLaps.map((lapId, i) => {
      const { startIdx } = distanceSliceIndices[i] || {};
      return startIdx || 0;
    });
    setCurrentIndices(indices);
    setReplayElapsedArr(Array(selectedLaps.length).fill(0));
    // Do not setIsPlaying(false) here; let user control play/pause
  }, [selectedLaps, timeRange, lapData]);

  // --- Lap data for plot ---
  const generateLapData = () => {
    const lapTraces = selectedLaps.map((lapId, index) => {
      const currentLap = lapData[lapId];
      const slice = distanceSliceIndices[index];
      if (!currentLap || !slice) return null;
      const { startIdx, endIdx } = slice;
      if (!Array.isArray(currentLap) || currentLap.length === 0 || startIdx === undefined || endIdx === undefined) return null;
      const filteredLap = currentLap.slice(startIdx, endIdx + 1);
      const x = filteredLap.map(p => p.position_x);
      const y = filteredLap.map(p => -p.position_z);
      const lapTimeMatch = lapId.match(/lap_(\d+)-(\d+)-(\d+)\.json/);
      const lapTime = lapTimeMatch 
        ? `${lapTimeMatch[1]}:${lapTimeMatch[2]}.${lapTimeMatch[3]}`
        : lapId;
      return {
        x: x,
        y: y,
        type: "scatter",
        mode: "lines",
        name: `Lap ${lapTime}`,
        line: {
          color: colors.lines[index % colors.lines.length],
          width: 2
        }
      };
    }).filter(Boolean);

    // Add a Plotly marker for the animated dot (one per lap)
    selectedLaps.forEach((lapId, index) => {
      const arr = lapData[lapId];
      const slice = distanceSliceIndices[index];
      if (!arr || !slice) return;
      const { startIdx, endIdx } = slice;
      let idx = currentIndices[index] ?? startIdx;
      if (!Array.isArray(arr) || arr.length === 0 || startIdx === undefined || endIdx === undefined) return;
      if (idx < startIdx) idx = startIdx;
      if (idx > endIdx) idx = endIdx;
      if (!arr[idx]) return;
      const p = arr[idx];
      lapTraces.push({
        x: [p.position_x],
        y: [-p.position_z],
        type: 'scatter',
        mode: 'markers',
        marker: {
          color: colors.lines[index % colors.lines.length],
          size: 12,
          symbol: 'circle',
          line: { color: '#fff', width: 2 },
        },
        name: `Replay Marker ${index+1}`,
        showlegend: false,
        hoverinfo: 'skip',
      });
    });
    return lapTraces;
  };

  // --- Border traces ---
  const generateBorderTraces = () => {
    if (!trackBorders) return [];
    const traces = [];
    if (Array.isArray(trackBorders.inside) && trackBorders.inside.length) {
      let x, y;
      if (Array.isArray(trackBorders.inside[0])) {
        x = trackBorders.inside.map(p => p[0]);
        y = trackBorders.inside.map(p => -p[1]);
      } else if (trackBorders.inside[0].position_x !== undefined) {
        x = trackBorders.inside.map(p => p.position_x);
        y = trackBorders.inside.map(p => -p.position_z);
      }
      if (x && y) {
        traces.push({
          x,
          y,
          type: 'scatter',
          mode: 'lines',
          name: 'Track Inside',
          line: { color: colors.border, width: 2 },
          showlegend: false
        });
      }
    }
    if (Array.isArray(trackBorders.outside) && trackBorders.outside.length) {
      let x, y;
      if (Array.isArray(trackBorders.outside[0])) {
        x = trackBorders.outside.map(p => p[0]);
        y = trackBorders.outside.map(p => -p[1]);
      } else if (trackBorders.outside[0].position_x !== undefined) {
        x = trackBorders.outside.map(p => p.position_x);
        y = trackBorders.outside.map(p => -p.position_z);
      }
      if (x && y) {
        traces.push({
          x,
          y,
          type: 'scatter',
          mode: 'lines',
          name: 'Track Outside',
          line: { color: colors.border, width: 2 },
          showlegend: false
        });
      }
    }
    return traces;
  };

  // --- Track bounding box ---
  useEffect(() => {
    let xs = [], ys = [];
    if (selectedLaps && selectedLaps.length > 0 && lapData[selectedLaps[0]]) {
      const lapArr = lapData[selectedLaps[0]];
      xs = lapArr.map(p => p.position_x);
      ys = lapArr.map(p => -p.position_z);
    } else if (trackBorders && trackBorders.inside && trackBorders.inside.length > 0) {
      xs = trackBorders.inside.map(p => Array.isArray(p) ? p[0] : p.position_x);
      ys = trackBorders.inside.map(p => Array.isArray(p) ? -p[1] : -p.position_z);
    }
    if (xs.length && ys.length) {
      setTrackBBox({
        xMin: Math.min(...xs),
        xMax: Math.max(...xs),
        yMin: Math.min(...ys),
        yMax: Math.max(...ys)
      });
    }
  }, [selectedLaps, lapData, trackBorders]);

  // Set initial axis ranges to show the full track when a new lap/track is loaded
  useEffect(() => {
    if (!fsUserZoomed && trackBBox.xMin !== undefined && trackBBox.xMax !== undefined && trackBBox.yMin !== undefined && trackBBox.yMax !== undefined) {
      setFsAxisRanges({
        x: [trackBBox.xMin, trackBBox.xMax],
        y: [trackBBox.yMin, trackBBox.yMax],
      });
    }
  }, [trackBBox, fsUserZoomed]);

  // --- Animation logic ---
  // Precompute time arrays for each lap
  const lapTimeArrays = React.useMemo(() => selectedLaps.map(lapId => getTimeArray(lapData[lapId])), [selectedLaps, lapData]);
  // Get max lap time for the selected range
  const maxReplayTime = React.useMemo(() => {
    let maxT = 0;
    lapTimeArrays.forEach((arr, i) => {
      const { startIdx, endIdx } = distanceSliceIndices[i];
      if (arr && arr.length > endIdx) {
        maxT = Math.max(maxT, arr[endIdx] - arr[startIdx]);
      }
    });
    return maxT;
  }, [lapTimeArrays, distanceSliceIndices]);

  // --- Animation frame (multi-lap, time-based, always matches lap time) ---
  useEffect(() => {
    if (!isPlaying) {
      // When pausing, store how much lap time has elapsed so far for each lap
      if (animationStartRef.current) {
        const now = performance.now();
        const newElapsedArr = selectedLaps.map((lapId, i) => {
          if (!animationStartRef.current[i]) return replayElapsedArr[i] || 0;
          // Only add the lap time that was played at the current speed
          const wallElapsed = (now - animationStartRef.current[i]) / 1000;
          return (replayElapsedArr[i] || 0) + wallElapsed * (animationStartRef.current._speed || 1);
        });
        setReplayElapsedArr(newElapsedArr);
      }
      animationStartRef.current = null;
      return;
    }
    if (!selectedLaps.length) return;
    let stopped = false;
    // Prepare per-lap animation start refs
    if (!animationStartRef.current || animationStartRef.current.length !== selectedLaps.length) {
      animationStartRef.current = Array(selectedLaps.length).fill(null);
    }
    // Store the speed at the time of play
    animationStartRef.current._speed = replaySpeed;
    function step(ts) {
      const indices = selectedLaps.map((lapId, i) => {
        const arr = lapData[lapId];
        const { startIdx, endIdx } = distanceSliceIndices[i] || {};
        const numPackets = endIdx - startIdx + 1;
        const freqInfo = estimatePacketFrequency(arr);
        const timeArr = getTimeArray(arr, freqInfo?.freq);
        const rangeStartTime = timeArr[startIdx] || 0;
        const rangeEndTime = timeArr[endIdx] || 0;
        const rangeLapTime = Math.max(0.01, rangeEndTime - rangeStartTime);
        if (!animationStartRef.current[i]) animationStartRef.current[i] = ts;
        const localReplayElapsed = replayElapsedArr[i] || 0;
        // Only apply the current speed to the time since play was pressed
        const wallElapsed = (ts - animationStartRef.current[i]) / 1000;
        const elapsed = localReplayElapsed + wallElapsed * replaySpeed;
        if (elapsed >= rangeLapTime) return endIdx;
        const frac = Math.min(elapsed / rangeLapTime, 1);
        return startIdx + Math.floor(frac * (numPackets - 1));
      });
      setCurrentIndices(indices);
      // Stop if all laps are done
      const allDone = selectedLaps.every((lapId, i) => {
        const arr = lapData[lapId];
        const { startIdx, endIdx } = distanceSliceIndices[i] || {};
        const freqInfo = estimatePacketFrequency(arr);
        const timeArr = getTimeArray(arr, freqInfo?.freq);
        const rangeStartTime = timeArr[startIdx] || 0;
        const rangeEndTime = timeArr[endIdx] || 0;
        const rangeLapTime = Math.max(0.01, rangeEndTime - rangeStartTime);
        if (!animationStartRef.current[i]) return false;
        const localReplayElapsed = replayElapsedArr[i] || 0;
        const elapsed = (ts - animationStartRef.current[i]) / 1000 + localReplayElapsed;
        return elapsed >= rangeLapTime;
      });
      if (allDone) {
        setIsPlaying(false);
        setReplayElapsedArr(Array(selectedLaps.length).fill(0));
        // Also reset marker positions to start (like pressing Reset)
        if (selectedLaps.length && lapData[selectedLaps[0]]) {
          const indices = selectedLaps.map((lapId, i) => {
            const { startIdx } = distanceSliceIndices[i] || {};
            return startIdx || 0;
          });
          setCurrentIndices(indices);
        } else {
          setCurrentIndices([]);
        }
        animationStartRef.current = null;
        return;
      }
      if (!stopped) {
        animationRef.current = requestAnimationFrame(step);
      }
    }
    animationRef.current = requestAnimationFrame(step);
    return () => {
      stopped = true;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, selectedLaps, lapData, distanceSliceIndices, timeRange, replaySpeed]);

  // --- Reset replay ---
  function handleReset() {
    setIsPlaying(false);
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    animationStartRef.current = null;
    if (selectedLaps.length && lapData[selectedLaps[0]]) {
      const indices = selectedLaps.map((lapId, i) => {
        const { startIdx } = distanceSliceIndices[i] || {};
        return startIdx || 0;
      });
      setCurrentIndices(indices);
    } else {
      setCurrentIndices([]);
    }
    setReplayElapsedArr(Array(selectedLaps.length).fill(0));
  }

  // --- Utility: Estimate packet frequency for the first selected lap ---
  function estimatePacketFrequency(lapArr) {
    if (!lapArr || lapArr.length < 2) return null;
    // Calculate total distance
    let totalDist = 0;
    for (let i = 1; i < lapArr.length; i++) {
      const p1 = lapArr[i - 1];
      const p2 = lapArr[i];
      const dx = (p2.position_x - p1.position_x) || 0;
      const dz = (p2.position_z - p1.position_z) || 0;
      totalDist += Math.sqrt(dx * dx + dz * dz);
    }
    // Calculate average speed (convert km/h to m/s)
    const speeds = lapArr.map(p => (p.car_speed || 0) * (1000/3600));
    const avgSpeed = speeds.reduce((a, b) => a + b, 0) / speeds.length;
    if (avgSpeed <= 0) return null;
    // Estimate total time
    const totalTime = totalDist / avgSpeed;
    // Estimate frequency
    const freq = lapArr.length / totalTime;
    return { freq, totalDist, avgSpeed, totalTime, numPackets: lapArr.length };
  }

  // In the component render, show the estimated frequency for the first selected lap
  const freqInfo = selectedLaps.length && lapData[selectedLaps[0]] ? estimatePacketFrequency(lapData[selectedLaps[0]]) : null;

  // --- Render ---
  return (
    <div style={{ width: '100%', marginBottom: '2rem', backgroundColor: colors.background, padding: '1rem', borderRadius: '8px' }}>
      {/* Track range slider */}
      <Box sx={{ mb: 2 }}>
        <TrackRangeSlider
          value={safeTrackRange}
          onChange={setTimeRange}
          onReset={handleResetTimeRange}
          color={colors.lines[0]}
          label="Track Range"
          corners={corners}
          selectedCornerIdx={selectedCornerIdx}
          onCornerSelect={handleCornerSelect}
        />
      </Box>
      {/* Replay controls and highlight dropdown */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { xs: 'flex-start', md: 'center' }, width: '100%' }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Button
              variant="contained"
              color={isPlaying ? 'error' : 'primary'}
              onClick={() => setIsPlaying(p => !p)}
            >
              {isPlaying ? 'Pause' : 'Play'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReset}
              sx={{
                minWidth: 0,
                px: 1.5,
                py: 0.5,
                fontSize: '0.95rem',
                ml: 0,
                height: '2rem',
                lineHeight: 1,
                alignSelf: 'center',
                boxShadow: 'none',
                borderColor: colors.lines[0],
                color: colors.lines[0],
                '&:hover': {
                  borderColor: colors.lines[0],
                  backgroundColor: 'rgba(0,174,239,0.08)'
                }
              }}
            >
              Reset
            </Button>
          </Stack>
          {/* Replay Speed Controls */}
          <Box sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 2, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
            <FormLabel component="legend" sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>Replay Speed</FormLabel>
            <RadioGroup
              row
              value={String(replaySpeed)}
              onChange={e => setReplaySpeed(Number(e.target.value))}
              aria-label="replay-speed"
              name="replay-speed-group"
            >
              <FormControlLabel value="0.5" control={<Radio sx={{ color: colors.lines[0], '&.Mui-checked': { color: colors.lines[0] } }} />} label={<span style={{ fontWeight: 700 }}>x0.5</span>} />
              <FormControlLabel value="1" control={<Radio sx={{ color: colors.lines[0], '&.Mui-checked': { color: colors.lines[0] } }} />} label={<span style={{ fontWeight: 700 }}>x1</span>} />
              <FormControlLabel value="2" control={<Radio sx={{ color: colors.lines[0], '&.Mui-checked': { color: colors.lines[0] } }} />} label={<span style={{ fontWeight: 700 }}>x2</span>} />
              <FormControlLabel value="4" control={<Radio sx={{ color: colors.lines[0], '&.Mui-checked': { color: colors.lines[0] } }} />} label={<span style={{ fontWeight: 700 }}>x4</span>} />
            </RadioGroup>
          </Box>
        </Stack>
        {/* Highlight Lap Dropdown (always rendered) */}
        <FormControl size="small" sx={{ minWidth: 180, background: '#222', borderRadius: 1, ml: { xs: 0, md: 2 }, mt: { xs: 2, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
          <InputLabel id="highlight-lap-label" sx={{ color: '#fff', fontWeight: 700 }}>Highlight Inputs</InputLabel>
          <Select
            labelId="highlight-lap-label"
            id="highlight-lap-select"
            value={highlightLapId || ""}
            label="Highlight Inputs"
            onChange={e => setHighlightLapId(e.target.value)}
            sx={{ color: '#fff', fontWeight: 700 }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: '#222',
                  color: '#fff',
                },
              },
            }}
          >
            <MenuItem value="" sx={{ fontWeight: 700 }}>None</MenuItem>
            {selectedLaps && selectedLaps.length > 0 ? (
              selectedLaps.map(lapId => {
                const lapTimeMatch = lapId.match(/lap_(\d+)-(\d+)-(\d+)\.json/);
                const lapTime = lapTimeMatch
                  ? `${lapTimeMatch[1]}:${lapTimeMatch[2]}.${lapTimeMatch[3]}`
                  : lapId;
                return (
                  <MenuItem key={lapId} value={lapId} sx={{ fontWeight: 700 }}>
                    {lapTime}
                  </MenuItem>
                );
              })
            ) : (
              <MenuItem value="_no_laps_" disabled sx={{ fontWeight: 700, color: '#888' }}>No laps available</MenuItem>
            )}
          </Select>
        </FormControl>
      </Stack>
      {/* Track detection debug info UI removed from here */}
      {/* Highlighted Race Line Plot or default multi-lap plot */}
      {highlightLapId ? (
        (() => {
          const highlightIndex = selectedLaps.findIndex(lapId => lapId === highlightLapId);
          const highlightSlice = highlightIndex !== -1 ? [distanceSliceIndices[highlightIndex]] : [];
          return (
            <RaceLineHighlight
              lapId={highlightLapId}
              lapData={lapData}
              trackLayout={trackLayout}
              distanceSliceIndices={highlightSlice}
              axisRanges={fsAxisRanges}
              onRelayout={figure => {
                if (
                  figure['xaxis.range[0]'] !== undefined && figure['xaxis.range[1]'] !== undefined &&
                  figure['yaxis.range[0]'] !== undefined && figure['yaxis.range[1]'] !== undefined
                ) {
                  setFsAxisRanges({
                    x: [figure['xaxis.range[0]'], figure['xaxis.range[1]']],
                    y: [figure['yaxis.range[0]'], figure['yaxis.range[1]']],
                  });
                  setAxisRanges({
                    x: [figure['xaxis.range[0]'], figure['xaxis.range[1]']],
                    y: [figure['yaxis.range[0]'], figure['yaxis.range[1]']],
                  });
                  setFsUserZoomed(true);
                }
              }}
              currentIndices={currentIndices}
              selectedLaps={selectedLaps}
              markerColor={colors.lines[selectedLaps.findIndex(lapId => lapId === highlightLapId) % colors.lines.length]}
            />
          );
        })()
      ) : (
        <div style={{ position: 'relative', width: '100%', height: '500px' }}>
          <Plot
            data={[
              ...generateLapData(),
              ...generateBorderTraces(),
              // REMOVED: autoTrackBorder overlay traces
            ]}
            layout={{
              autosize: true,
              width: null,
              height: 500,
              margin: { l: 40, r: 40, t: 40, b: 40 },
              dragmode: 'pan',
              xaxis: {
                title: 'X Position',
                gridcolor: colors.grid,
                zerolinecolor: colors.grid,
                range: fsAxisRanges?.x,
              },
              yaxis: {
                title: 'Y Position',
                gridcolor: colors.grid,
                zerolinecolor: colors.grid,
                scaleanchor: 'x',
                range: fsAxisRanges?.y,
              },
              plot_bgcolor: colors.background,
              paper_bgcolor: colors.background,
              font: { color: colors.text },
              showlegend: true,
              legend: {
                orientation: 'h',
                yanchor: 'bottom',
                y: 1.02,
                xanchor: 'center',
                x: 0.5,
                bgcolor: colors.background,
                bordercolor: colors.grid,
                borderwidth: 1,
              },
            }}
            config={{
              responsive: true,
              displayModeBar: true, // Show Plotly modebar controls
              scrollZoom: true,
            }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
            onRelayout={figure => {
              // Save axis ranges on user zoom/pan
              if (figure['xaxis.range[0]'] !== undefined && figure['xaxis.range[1]'] !== undefined &&
                  figure['yaxis.range[0]'] !== undefined && figure['yaxis.range[1]'] !== undefined) {
                setFsAxisRanges({
                  x: [figure['xaxis.range[0]'], figure['xaxis.range[1]']],
                  y: [figure['yaxis.range[0]'], figure['yaxis.range[1]']],
                });
                setAxisRanges({
                  x: [figure['xaxis.range[0]'], figure['xaxis.range[1]']],
                  y: [figure['yaxis.range[0]'], figure['yaxis.range[1]']],
                });
                setFsUserZoomed(true);
              }
            }}
          />
        </div>
      )}
    </div>
  );
}

RaceLineReplay.propTypes = {
  timeRange: PropTypes.object.isRequired,
  setTimeRange: PropTypes.func.isRequired,
  handleResetTimeRange: PropTypes.func.isRequired,
  selectedLaps: PropTypes.array.isRequired,
  lapData: PropTypes.object.isRequired,
  trackLayout: PropTypes.object,
  normalizedTracks: PropTypes.object, // NEW PROP
  onAutoDetectTrack: PropTypes.func, // NEW PROP
  onTrackDetectStatus: PropTypes.func, // NEW PROP
};
